import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { selectDynamicFieldsNamesById } from 'store/forms/selectors';

import {
  FILE_LOAD_FAILED_MESSAGE,
  FILE_LOADING_LABEL,
  HTML_FIELDS_MARKED_NOTE,
  HTML_FRAME_SANDBOX,
  HTML_FRAME_TITLE,
  TemplateRequestStatus,
} from '../../FormChangeHistory.constants';
import { FormTemplateRequestsContext } from '../../FormChangeHistory.context';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import { markHtmlTemplateFields } from '../../FormChangeHistory.utils';
import useTemplateRequest from '../../hooks/useTemplateRequest';
import TemplateRequestState from '../TemplateRequestState';

import {
  htmlFrameStyles,
  htmlViewStyles,
  TEMPLATE_FIELD_MARK_CSS,
} from './TemplateHtmlView.styles';

/**
 * One side's HTML template, rendered as `srcdoc` in a fully sandboxed frame: a stored template is
 * untrusted markup, so it runs no script, submits no form and gets an opaque origin. Its dynamic
 * fields are named from the form's dynamic-field schema, which the form page has already read, and
 * its signatures by signer.
 */
const TemplateHtmlView = ({ auditId, side }) => {
  const requests = useContext(FormTemplateRequestsContext);

  const dynamicFieldsNamesById = useSelector(selectDynamicFieldsNamesById);

  const loadHtml = useCallback(
    () => requests.getFile(auditId, side).then(({ data }) => data.text()),
    [requests, auditId, side],
  );

  const { status, data: html, retry } = useTemplateRequest(loadHtml);

  const markedHtml = useMemo(
    () =>
      html
        ? markHtmlTemplateFields({ html, dynamicFieldsNamesById, markCss: TEMPLATE_FIELD_MARK_CSS })
        : html,
    [html, dynamicFieldsNamesById],
  );

  if (status !== TemplateRequestStatus.LOADED) {
    return (
      <TemplateRequestState
        failedMessage={FILE_LOAD_FAILED_MESSAGE}
        loadingLabel={FILE_LOADING_LABEL}
        status={status}
        onRetry={retry}
      />
    );
  }

  return (
    <Box sx={htmlViewStyles}>
      <Typography color="text.secondary" variant="regular12">
        {HTML_FIELDS_MARKED_NOTE}
      </Typography>
      <Box
        component="iframe"
        data-testid={FormChangeHistoryTestIds.HTML_FRAME}
        referrerPolicy="no-referrer"
        sandbox={HTML_FRAME_SANDBOX}
        srcDoc={markedHtml}
        sx={htmlFrameStyles}
        title={HTML_FRAME_TITLE}
      />
    </Box>
  );
};

TemplateHtmlView.propTypes = {
  auditId: PropTypes.string.isRequired,
  side: PropTypes.string.isRequired,
};

export default TemplateHtmlView;
